import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/FormPage.css';

const FormConfigForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    key: '',
    title: '',
    description: '',
    is_active: true,
    config_json: '',
  });

  useEffect(() => {
    if (isEditMode) {
      loadFormConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadFormConfig = async () => {
    try {
      setLoading(true);
      const response = await api.getFormConfigById(id);
      const config = response.data;
      setFormData({
        key: config.key || '',
        title: config.title || '',
        description: config.description || '',
        is_active: config.is_active !== undefined ? config.is_active : true,
        config_json: config.config_json ? JSON.stringify(config.config_json, null, 2) : '',
      });
    } catch (err) {
      setError('Failed to load form configuration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      // Validate JSON
      let configJson = {};
      if (formData.config_json.trim()) {
        try {
          configJson = JSON.parse(formData.config_json);
        } catch (e) {
          setError('Invalid JSON format in configuration');
          setLoading(false);
          return;
        }
      }

      const submitData = {
        key: formData.key,
        title: formData.title,
        description: formData.description,
        is_active: formData.is_active,
        config_json: configJson,
      };

      if (isEditMode) {
        await api.updateFormConfig(id, submitData);
        setSuccess('Form configuration updated successfully!');
      } else {
        await api.createFormConfig(submitData);
        setSuccess('Form configuration created successfully!');
      }

      setTimeout(() => {
        navigate('/form-configs');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to save form configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/form-configs');
  };

  if (loading && isEditMode) {
    return (
      <div className="form-page-container">
        <div className="loading">Loading form configuration...</div>
      </div>
    );
  }

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <div>
          <h1>{isEditMode ? 'Edit Form Configuration' : 'Add New Form Configuration'}</h1>
          <p className="form-page-subtitle">
            {isEditMode ? 'Update form configuration settings' : 'Create a new form configuration'}
          </p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3 className="form-section-title">Basic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Key *</label>
                <input
                  type="text"
                  name="key"
                  value={formData.key}
                  onChange={handleChange}
                  required
                  disabled={loading || isEditMode}
                  placeholder="unique-form-key"
                />
                <small>Unique identifier for the form (cannot be changed after creation)</small>
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="Form Title"
                />
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  disabled={loading}
                  placeholder="Brief description of the form"
                />
              </div>

              <div className="form-group full-width">
                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    Active
                  </label>
                </div>
                <small>Only active forms are available for use</small>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Form Configuration (JSON)</h3>
            <div className="form-group">
              <label>Configuration JSON</label>
              <textarea
                name="config_json"
                value={formData.config_json}
                onChange={handleChange}
                rows="15"
                disabled={loading}
                placeholder={`{\n  "fields": [\n    {\n      "name": "field_name",\n      "type": "text",\n      "label": "Field Label",\n      "required": true\n    }\n  ]\n}`}
                style={{ fontFamily: 'monospace', fontSize: '13px' }}
              />
              <small>Enter form configuration in JSON format</small>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEditMode ? 'Update Configuration' : 'Create Configuration')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormConfigForm;

